import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import {
    Bold,
    Italic,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    Link as LinkIcon,
    Undo2,
    Redo2,
    Minus,
    Strikethrough
} from "lucide-react";

function ToolButton({ onClick, active, disabled, label, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
            data-testid={testId}
            className={`w-9 h-9 flex items-center justify-center border transition-colors ${
                active
                    ? "bg-acre-primary text-white border-acre-primary"
                    : "bg-white text-acre-ink border-acre-line hover:border-acre-primary"
            } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
        >
            {children}
        </button>
    );
}

function Divider() {
    return <div className="w-px h-6 bg-acre-line mx-1" aria-hidden />;
}

export default function RichTextEditor({ value, onChange, placeholder = "Write the article…" }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
                bulletList: {},
                orderedList: {},
                blockquote: {},
                horizontalRule: {},
                link: false,
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
            }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || "",
        editorProps: {
            attributes: {
                class:
                    "prose-editor min-h-[420px] focus:outline-none px-6 py-6 text-acre-ink text-[17px] leading-[1.7]",
                "data-testid": "rich-text-editor",
            },
        },
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
    });

    // If parent replaces `value` externally (e.g., loading an existing post),
    // sync it into the editor.
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if ((value || "") !== current) {
            editor.commands.setContent(value || "", false);
        }
    }, [value, editor]);

    if (!editor) return null;

    const promptLink = () => {
        const previous = editor.getAttributes("link").href || "";
        const url = window.prompt("URL", previous);
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    return (
        <div className="border border-acre-line bg-white flex flex-col max-h-[70vh]" data-testid="rich-text-wrapper">
            <div className="flex flex-wrap items-center gap-1 border-b border-acre-line p-2 bg-[#FBFAF8] shrink-0">
                <ToolButton
                    testId="tt-bold"
                    label="Bold"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive("bold")}
                >
                    <Bold size={15} />
                </ToolButton>
                <ToolButton
                    testId="tt-italic"
                    label="Italic"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive("italic")}
                >
                    <Italic size={15} />
                </ToolButton>
                <ToolButton
                    testId="tt-strike"
                    label="Strikethrough"
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    active={editor.isActive("strike")}
                >
                    <Strikethrough size={15} />
                </ToolButton>
                <Divider />
                <ToolButton
                    testId="tt-h2"
                    label="Heading 2"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    active={editor.isActive("heading", { level: 2 })}
                >
                    <Heading2 size={15} />
                </ToolButton>
                <ToolButton
                    testId="tt-h3"
                    label="Heading 3"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    active={editor.isActive("heading", { level: 3 })}
                >
                    <Heading3 size={15} />
                </ToolButton>
                <Divider />
                <ToolButton
                    testId="tt-ul"
                    label="Bullet list"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    active={editor.isActive("bulletList")}
                >
                    <List size={15} />
                </ToolButton>
                <ToolButton
                    testId="tt-ol"
                    label="Numbered list"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    active={editor.isActive("orderedList")}
                >
                    <ListOrdered size={15} />
                </ToolButton>
                <ToolButton
                    testId="tt-quote"
                    label="Blockquote"
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    active={editor.isActive("blockquote")}
                >
                    <Quote size={15} />
                </ToolButton>
                <ToolButton
                    testId="tt-hr"
                    label="Horizontal rule"
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                >
                    <Minus size={15} />
                </ToolButton>
                <Divider />
                <ToolButton
                    testId="tt-link"
                    label="Link"
                    onClick={promptLink}
                    active={editor.isActive("link")}
                >
                    <LinkIcon size={15} />
                </ToolButton>
                <Divider />
                <ToolButton
                    testId="tt-undo"
                    label="Undo"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                >
                    <Undo2 size={15} />
                </ToolButton>
                <ToolButton
                    testId="tt-redo"
                    label="Redo"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                >
                    <Redo2 size={15} />
                </ToolButton>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="rich-text-scroll">
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}
